import { env } from "@/lib/env";

/**
 * The one HTTP client. No component, hook or page may call fetch/axios
 * directly — everything goes through a typed module in src/api/.
 *
 * Responsibilities, in order:
 *   1. unwrap bella-admin-api's success envelope down to `data`
 *   2. turn its error envelope into a typed ApiError
 *   3. carry the access token in and out of the Authorization header
 *   4. refresh once on 401, single-flight, then retry the original request —
 *      and end the session only when the API refuses the refresh, never when
 *      it merely could not answer
 */

/** Success envelope: core/nest/response.interceptor.ts */
interface SuccessEnvelope<T> {
  success: true;
  message: string;
  data: T;
  /** Always absent on the Nest entrypoint — requestIdMiddleware is Express-only. */
  traceId?: string;
  timestamp: string;
}

/** Error envelope: core/nest/http-exception.filter.ts */
interface ErrorEnvelope {
  success: false;
  message: string;
  /** The field is `code`, NOT `errorCode`. Absent on class-validator 400s. */
  code?: string;
  details?: Record<string, unknown>;
  timestamp: string;
}

export class ApiError extends Error {
  readonly status: number;
  /** `INVALID_CREDENTIALS`, `FORBIDDEN`, `TOKEN_EXPIRED`, … or undefined. */
  readonly code: string | undefined;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    status: number,
    message: string,
    code?: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True when the request never reached the API (offline, DNS, CORS, crash). */
  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

/* ------------------------------------------------------------------ *
 * Access token — memory only.
 *
 * Deliberately NOT localStorage: a token there survives every tab and
 * outlives the session, so one XSS is a durable credential theft. Here the
 * worst case is bounded by the page's lifetime. The cost is that a hard
 * reload has no token, which is what the boot-time refresh in use-auth
 * exists to solve. See docs/modules/auth.md.
 * ------------------------------------------------------------------ */
let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/** Called when refresh fails — the session is over and the app must react. */
type SessionExpiredHandler = () => void;
let onSessionExpired: SessionExpiredHandler = () => {};

export function setSessionExpiredHandler(handler: SessionExpiredHandler): void {
  onSessionExpired = handler;
}

/**
 * The API returns a new access token in the Authorization *response* header
 * rather than the body, on login, register and refresh alike. Reading it here
 * means every caller gets the rotation for free.
 *
 * Requires `exposedHeaders: ["Authorization", "X-Expires-In"]` on the server —
 * without it this silently reads null on a cross-origin response and the app
 * behaves as if login never happened.
 */
function captureTokenFrom(response: Response): void {
  const header = response.headers.get("Authorization");
  if (header?.startsWith("Bearer ")) {
    setAccessToken(header.slice(7));
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Skip the 401-refresh-retry interceptor. Set on the auth endpoints
   * themselves: a wrong password on /auth/login must surface as a 401 to the
   * form, not trigger a refresh, and refreshing the refresh call would loop.
   */
  skipAuthRefresh?: boolean;
}

async function rawRequest(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: "application/json" };

  // FormData (file uploads) carries its own multipart boundary; the browser
  // sets that Content-Type itself.
  const isForm = options.body instanceof FormData;
  if (options.body !== undefined && !isForm) {
    headers["Content-Type"] = "application/json";
  }
  if (accessToken !== null) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const init: RequestInit = {
    method: options.method ?? "GET",
    headers,
    // Sends and accepts the httpOnly refreshToken cookie. Requires
    // credentials:true + an exact-origin allowlist on the server; a wildcard
    // CORS origin would make the browser drop the cookie.
    credentials: "include",
  };
  if (options.body !== undefined) {
    init.body = isForm ? (options.body as FormData) : JSON.stringify(options.body);
  }
  if (options.signal) init.signal = options.signal;

  try {
    return await fetch(`${env.VITE_API_BASE_URL}${path}`, init);
  } catch (cause) {
    // fetch only rejects for transport-level failures — offline, DNS, blocked
    // by CORS, server down. Status 0 marks that apart from any HTTP answer.
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError(0, "Could not reach the server.", "NETWORK_ERROR");
  }
}

async function parseEnvelope<T>(response: Response): Promise<T> {
  captureTokenFrom(response);

  // 204 and empty bodies are valid: several endpoints answer with data: null.
  const text = await response.text();
  if (text === "") {
    if (response.ok) return null as T;
    throw new ApiError(response.status, response.statusText || "Request failed");
  }

  let payload: unknown;
  try {
    payload = JSON.parse(text);
  } catch {
    // An HTML error page from a proxy, typically. Never surface the body.
    throw new ApiError(response.status, "The server returned an unexpected response.");
  }

  const envelope = payload as SuccessEnvelope<T> | ErrorEnvelope;

  if (!response.ok || envelope.success === false) {
    const error = envelope as ErrorEnvelope;
    throw new ApiError(
      response.status,
      error.message || "Request failed",
      error.code,
      error.details,
    );
  }

  // Narrowed to SuccessEnvelope<T> by the `success === false` check above.
  return envelope.data;
}

/* ------------------------------------------------------------------ *
 * Single-flight refresh.
 *
 * A screen that fires three queries at once gets three 401s. Without this
 * they would each refresh, and the last two would present an already-rotated
 * refresh token. One promise, shared by every waiter.
 * ------------------------------------------------------------------ */
/**
 * How a refresh ended. Only `rejected` ends the session: the API read the
 * refresh cookie and refused it. `unavailable` means it could not answer —
 * the connection dropped, bella was restarting (502/503), or it rate-limited
 * the call (429). Signing the admin out for that would throw away a session
 * that is still good, which is how a busy office used to be logged out
 * together.
 */
export type RefreshResult =
  | { outcome: "refreshed" }
  | { outcome: "rejected" }
  | { outcome: "unavailable"; error: ApiError };

let refreshInFlight: Promise<RefreshResult> | null = null;

async function performRefresh(): Promise<RefreshResult> {
  let response: Response;
  try {
    response = await rawRequest("/auth/refresh", {
      method: "POST",
      skipAuthRefresh: true,
    });
  } catch (cause) {
    // rawRequest turns every transport failure into a status-0 ApiError.
    const error =
      cause instanceof ApiError
        ? cause
        : new ApiError(0, "Could not reach the server.", "NETWORK_ERROR");
    return { outcome: "unavailable", error };
  }

  if (response.status === 401 || response.status === 403) {
    return { outcome: "rejected" };
  }
  if (!response.ok) {
    return {
      outcome: "unavailable",
      error: new ApiError(
        response.status,
        "Couldn't renew your session. Please try again.",
      ),
    };
  }

  captureTokenFrom(response);
  // The token arrives in the header, so a 200 with no readable Authorization
  // means exposedHeaders is misconfigured. Treat it as rejected rather than
  // looping on a token we cannot see.
  return getAccessToken() !== null ? { outcome: "refreshed" } : { outcome: "rejected" };
}

export function refreshSession(): Promise<RefreshResult> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/** Sends a request, refreshing once on 401; returns the raw response. */
async function send(path: string, options: RequestOptions): Promise<Response> {
  const response = await rawRequest(path, options);

  if (response.status === 401 && !options.skipAuthRefresh) {
    // Drain the body so the connection is released before the retry.
    void response.text().catch(() => undefined);

    const refresh = await refreshSession();
    if (refresh.outcome === "rejected") {
      setAccessToken(null);
      onSessionExpired();
      throw new ApiError(
        401,
        "Your session has expired. Please sign in again.",
        "SESSION_EXPIRED",
      );
    }
    // The API could not be asked. The session is probably fine: keep it, and
    // fail this one request the way any offline, 429 or 5xx request fails.
    // The next request tries the refresh again.
    if (refresh.outcome === "unavailable") throw refresh.error;

    // Exactly one retry. A second 401 is a real authorisation failure, not a
    // stale token, and retrying again would loop.
    return rawRequest(path, options);
  }

  return response;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return parseEnvelope<T>(await send(path, options));
}

/**
 * A binary response — a call recording — through the same auth and refresh
 * path as every JSON call. A failure still arrives as the JSON error
 * envelope, and becomes the same ApiError.
 */
export async function requestBlob(
  path: string,
  options: Omit<RequestOptions, "method" | "body"> = {},
): Promise<Blob> {
  const response = await send(path, { ...options, method: "GET" });
  if (!response.ok) {
    await parseEnvelope<never>(response);
    throw new ApiError(response.status, "Request failed");
  }
  captureTokenFrom(response);
  return response.blob();
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "GET" }),
  post: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, "method" | "body">,
  ) => request<T>(path, { ...options, method: "POST", body }),
  patch: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, "method" | "body">,
  ) => request<T>(path, { ...options, method: "PATCH", body }),
  put: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, "method" | "body">,
  ) => request<T>(path, { ...options, method: "PUT", body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "DELETE" }),
};
